import { ProcessSubscriptionCatalogDocument } from '@6grain/masofa-monolith-api-client/common/model/processSubscriptionCatalogDocument';

export const SATELLITE_FIND_PRODUCTS_KEY = 'satellite.find-products';
export const SATELLITE_DOWNLOAD_PRODUCTS_KEY = 'satellite.download-found-products';
export const DOWNLOAD_CONTOUR_POLL_INTERVAL_MS = 1500;
export const DOWNLOAD_CONTOUR_POLL_ATTEMPTS = 21;
export const DOWNLOAD_CONTOUR_PAGE_SIZE = 200;
export const DOWNLOAD_CONTOUR_MAX_PAGES = 50;

const RESTART_OWED_PREFIX = 'masofa.satelliteContourRestart:';

const CANCEL_STATES = new Set(['Pending', 'Running']);
const BLOCK_STATES = new Set(['Pending', 'Running', 'Cancelling']);

export function isSatelliteContourProcess(processKey?: string | null): boolean {
  return processKey === SATELLITE_FIND_PRODUCTS_KEY
    || processKey === SATELLITE_DOWNLOAD_PRODUCTS_KEY;
}

export function markContourRestartOwed(subscriptionId: string): void {
  writeRestartOwed(subscriptionId, true);
}

export function clearContourRestartOwed(subscriptionId: string): void {
  writeRestartOwed(subscriptionId, false);
}

export function isContourRestartOwed(subscriptionId: string): boolean {
  try {
    return sessionStorage.getItem(RESTART_OWED_PREFIX + subscriptionId) === '1';
  } catch {
    return false;
  }
}

export function catalogParametersForSubscription(
  document: ProcessSubscriptionCatalogDocument | null | undefined,
  subscriptionId: string,
): Record<string, unknown> | null {
  const row = document?.subscriptions?.find(item => item.id === subscriptionId);
  return asParameters(row?.parameters);
}

export function geometrySignature(parameters: unknown): string {
  const geometry = readGeometry(parameters);
  if (!geometry) {
    return '';
  }
  const regionIds = geometry['regionIds'] ?? geometry['RegionIds'];
  return JSON.stringify({
    sourceType: text(geometry['sourceType'] ?? geometry['SourceType']),
    regionIds: Array.isArray(regionIds) ? regionIds.map(item => String(item)).sort() : [],
    countryName: text(geometry['countryName'] ?? geometry['CountryName']),
    geoJson: text(geometry['geoJson'] ?? geometry['GeoJson']),
    geoJsonFileId: text(geometry['geoJsonFileId'] ?? geometry['GeoJsonFileId']),
  });
}

export function downloadContourShouldRestart(options: {
  mode: 'create' | 'edit' | 'copy';
  processKey: string;
  contourLoaded: boolean;
  baselineSignature: string | null;
  restartOwed?: boolean;
  parameters?: Record<string, unknown>;
}): boolean {
  if (options.mode !== 'edit' || !isSatelliteContourProcess(options.processKey)) {
    return false;
  }
  if (!options.contourLoaded || options.baselineSignature == null || !options.parameters) {
    return false;
  }
  if (options.restartOwed) {
    return true;
  }
  return geometrySignature(options.parameters) !== options.baselineSignature;
}

export function geometrySourceType(parameters: unknown): string {
  const geometry = readGeometry(parameters);
  return text(geometry?.['sourceType'] ?? geometry?.['SourceType']);
}

export function preserveSatelliteLookback(
  processKey: string,
  parameters: Record<string, unknown>,
  loaded: Record<string, unknown> | null,
): Record<string, unknown> {
  if (processKey !== SATELLITE_FIND_PRODUCTS_KEY || !loaded) {
    return parameters;
  }
  const lookback = loaded['lookback'] ?? loaded['Lookback'];
  if (typeof lookback !== 'string' || !lookback.trim()) {
    return parameters;
  }
  return { ...parameters, lookback };
}

export function readStartedAssignmentId(value: unknown): string {
  if (!value || typeof value !== 'object') {
    return '';
  }
  const record = value as Record<string, unknown>;
  const assignmentId = record['assignmentId'] ?? record['AssignmentId'];
  return typeof assignmentId === 'string' ? assignmentId : '';
}

export function isNewContourAssignment(startedId: string, previousInflightIds: readonly string[]): boolean {
  return !!startedId && !previousInflightIds.includes(startedId);
}

export function inflightAssignmentIds(
  items: ReadonlyArray<{ id?: string | null; state?: string | null }> | null | undefined,
  kind: 'cancel' | 'block',
): string[] {
  const states = kind === 'cancel' ? CANCEL_STATES : BLOCK_STATES;
  return (items ?? [])
    .filter(item => !!item.id && states.has(item.state ?? ''))
    .map(item => item.id as string);
}

function readGeometry(parameters: unknown): Record<string, unknown> | null {
  const record = asParameters(parameters);
  if (!record) {
    return null;
  }
  return asParameters(record['geometry'] ?? record['Geometry']);
}

function asParameters(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      return asParameters(JSON.parse(value));
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function writeRestartOwed(subscriptionId: string, owed: boolean): void {
  try {
    const key = RESTART_OWED_PREFIX + subscriptionId;
    if (owed) {
      sessionStorage.setItem(key, '1');
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // The next Save still compares geometry when storage is blocked.
  }
}
